import { interpolate } from 'remotion';

// Types
import { EmployeeCertification } from '@/types';
import { CertificateTiming, VisibleCertificate } from './types';

// Constants
import { DATE_FORMATS } from '@shared/constants';
import { CLAMP_BOTH } from '@/constants';
import {
  BASE_ENTRY_DURATION,
  DATE_CHARS_PER_FRAME,
  HOLD_DURATION_AFTER_COMPLETE,
  MAX_DISPLAY_CERTIFICATES,
  MIN_CERTIFICATE_DURATION,
  TITLE_CHARS_PER_FRAME,
  TRANSITION_DURATION,
} from './constants';

// Utils
import { formatDate } from '@shared/utils';

/**
 * Calculate the display duration for a single certificate based on content length.
 */
export const calculateCertificateDuration = (
  certificate: EmployeeCertification,
): number => {
  const name = certificate.name ?? '';
  const issueText = formatDate(
    certificate.issue,
    DATE_FORMATS.SHORT_MONTH_DAY_YEAR,
  );

  const titleTypingDuration = Math.ceil(name.length / TITLE_CHARS_PER_FRAME);
  const dateTypingDuration = Math.ceil(issueText.length / DATE_CHARS_PER_FRAME);

  const naturalDuration =
    BASE_ENTRY_DURATION +
    titleTypingDuration +
    dateTypingDuration +
    HOLD_DURATION_AFTER_COMPLETE;

  return Math.max(naturalDuration, MIN_CERTIFICATE_DURATION);
};

/**
 * Assign sequential start/transition/end frames to each certificate.
 * Each certificate starts exiting as the next one enters.
 */
export const calculateCertificateTimings = (
  certificates: EmployeeCertification[],
): CertificateTiming[] => {
  const timings: CertificateTiming[] = [];
  let currentFrame = 0;

  certificates.forEach((certificate, index) => {
    const duration = calculateCertificateDuration(certificate);
    const hasConfettiAnimation =
      index === Math.min(MAX_DISPLAY_CERTIFICATES, certificates.length) - 1;
    const startFrame = currentFrame;
    const transitionStartFrame = startFrame + duration;
    const endFrame =
      transitionStartFrame +
      TRANSITION_DURATION +
      (hasConfettiAnimation ? 100 : 0);

    timings.push({
      startFrame,
      transitionStartFrame,
      endFrame,
    });

    currentFrame = transitionStartFrame;
  });

  return timings;
};

/**
 * Calculate total Scene6 duration from all certificate timings.
 * Returns the last certificate's endFrame, or a fallback of 300 if no certificates.
 */
export const calculateTotalScene6Duration = (
  certificates: EmployeeCertification[] = [],
): number => {
  if (certificates.length === 0) return 0; // Skip Scene 6 when no certificates

  const limited = getRecentCertificates(certificates);
  const timings = calculateCertificateTimings(limited);

  return timings.at(-1)?.endFrame ?? 300;
};

/**
 * Get up to MAX_DISPLAY_CERTIFICATES certificates (most recent by issue date).
 */
export const getRecentCertificates = (
  certificates: EmployeeCertification[],
): EmployeeCertification[] => {
  if (!certificates || certificates.length === 0) return [];

  // Filter out certificates without a name
  const validCertificates = certificates.filter(
    (cert) => cert.name && cert.name.trim() !== '',
  );

  if (validCertificates.length <= MAX_DISPLAY_CERTIFICATES)
    return validCertificates;

  return validCertificates.slice(0, MAX_DISPLAY_CERTIFICATES);
};

/**
 * Opacity when exiting: fades from 1 to 0 in the first half of the exit duration.
 */
export const getExitOpacity = (exitProgress: number): number =>
  interpolate(exitProgress, [0, 0.5], [1, 0], CLAMP_BOTH);

/**
 * Get visible certificates at the current frame, with timing and layout info.
 */
export const getVisibleCertificates = ({
  certificates,
  currentFrame,
}: {
  certificates: EmployeeCertification[];
  currentFrame: number;
}): VisibleCertificate[] => {
  const limited = getRecentCertificates(certificates);
  const timings = calculateCertificateTimings(limited);

  return limited
    .map((certificate, index): VisibleCertificate | null => {
      const timing = timings[index];
      if (!timing) return null;

      const isLastCertificate = index === limited.length - 1;
      const isVisible = currentFrame >= timing.startFrame;

      // Last certificate stays on screen (no exit animation)
      const transitionStartFrame = !isLastCertificate
        ? timing.transitionStartFrame
        : undefined;

      return {
        certificate,
        index,
        isVisible,
        startFrame: timing.startFrame,
        transitionStartFrame,
        isFlipped: index % 2 === 1,
      };
    })
    .filter((item): item is VisibleCertificate => item !== null);
};
